import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Checkbox, Button } from '@percent/lemonade'
import type { SmartApplyPartner } from '@percent/cause-dashboard/constants/smartApplyPartnerConfig'
import { useSmartApplyTracking } from '@percent/cause-dashboard/common/hooks/useSmartApplyTracking'
import { useEnabledSmartApplyPartners } from '@percent/cause-dashboard/common/hooks/useEnabledSmartApplyPartners/useEnabledSmartApplyPartners'
import styles from '../../explicitSmartApply/smartApplyShared.module.scss'

const toKebabCase = (str: string) => str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()

type SecondSmartApplyPageProps = {
  partners: SmartApplyPartner[]
  setSelectedPartner: (partner: SmartApplyPartner) => void
  appliedPartners: Set<SmartApplyPartner>
  onApply: (action: 'applied') => void
  onDismiss: (action: 'dismissed') => void
  registerDismissCallback?: (callback: (() => void) | null) => void
}

export function SecondSmartApplyPage({
  partners,
  setSelectedPartner,
  appliedPartners,
  onApply,
  onDismiss,
  registerDismissCallback
}: Readonly<SecondSmartApplyPageProps>) {
  const { t } = useTranslation()
  const { trackProductApplied, trackDismissed } = useSmartApplyTracking({ variant: 'second_smart_apply' })
  const { enabledPartners, getConfig } = useEnabledSmartApplyPartners()

  const enabledSet = useMemo(() => new Set(enabledPartners), [enabledPartners])
  const partnerSet = useMemo(() => new Set(partners), [partners])

  const products = useMemo(
    () =>
      partners
        .filter(p => partnerSet.has(p) && enabledSet.has(p))
        .map(p => {
          const config = getConfig(p)
          return {
            name: p,
            displayName: String(t(config.i18n.card.title as never)),
            description: String(t(config.i18n.card.description as never)),
            logo: config.logo
          }
        }),
    [t, partners, partnerSet, enabledSet, getConfig]
  )

  const [applied, setApplied] = useState<Set<SmartApplyPartner>>(new Set(appliedPartners))

  useEffect(() => {
    setApplied(new Set(appliedPartners))
  }, [appliedPartners])

  useEffect(() => {
    registerDismissCallback?.(() => {
      trackDismissed({ appliedProductsCount: appliedPartners.size })
    })
    return () => registerDismissCallback?.(null)
  }, [registerDismissCallback, trackDismissed, appliedPartners.size])

  const handleProductToggle = (partnerName: SmartApplyPartner) => {
    setApplied(prev => new Set(prev).add(partnerName))
    setSelectedPartner(partnerName)
    trackProductApplied({ productName: partnerName })
    onApply('applied')
  }

  const handleDismiss = () => {
    trackDismissed({ appliedProductsCount: applied.size })
    onDismiss('dismissed')
  }

  const getButtonText = () => {
    if (applied.size === 0) return t('typography.smartApply.notInterested')
    return t('button.close')
  }

  return (
    <div className={styles.smartApplyContainer}>
      <div className={styles.contentWrapper}>
        <Typography className={styles.title} variant="h2">
          <span role="img" aria-label="sparkles">
            ✨
          </span>{' '}
          {t('typography.smartApply.title')}
        </Typography>

        <div className={styles.subtitles}>
          <Typography variant="large">
            <div
              dangerouslySetInnerHTML={{
                __html: t('typography.smartApply.subtitle1')
              }}
            />
          </Typography>
        </div>

        <div className={styles.checkboxesContainer}>
          {products.map(product => {
            const isApplied = applied.has(product.name)

            return (
              <button
                type="button"
                key={product.name}
                className={`${styles.productCard} ${isApplied ? styles.appliedCard : ''}`}
                disabled={isApplied}
                onClick={() => {
                  if (!isApplied) handleProductToggle(product.name)
                }}
                onKeyDown={e => {
                  if (!isApplied && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    handleProductToggle(product.name)
                  }
                }}
                data-testid={`${toKebabCase(product.name)}-product-card`}
              >
                <img src={product.logo} alt={product.displayName} className={styles.productLogo} />
                <div className={styles.productInfo}>
                  <Typography variant="default-bold" className={styles.productTitle}>
                    {product.displayName}
                  </Typography>
                  <Typography $color="gray300" variant="default" className={styles.productDescription}>
                    {product.description}
                  </Typography>
                </div>
                {isApplied ? (
                  <Checkbox
                    name={product.name}
                    variant="default"
                    label=""
                    onChange={() => handleProductToggle(product.name)}
                    active
                    dataTestId={`${toKebabCase(product.name)}-checkbox`}
                    className={styles.productCheckbox}
                  />
                ) : (
                  <Typography variant="default" className={styles.clickToApply}>
                    {t('button.apply')}
                  </Typography>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <div className={`${styles.buttonWrapper} ${styles.applyButtonWrapper}`}>
        <Button variant="tertiary" stretch onClick={handleDismiss} data-testid="second-smart-apply-dismiss-button">
          {getButtonText()}
        </Button>
      </div>
    </div>
  )
}
